import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, AlertCircle, ExternalLink, Plus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Conveyancing external-documents tracking panel (Phase Conv-1b.2).
 *
 * TYPE 2 documents (uploaded by 3rd parties — Municipality, Body Corporate,
 * Master Electrician, Gas Installer, Bond Attorney). State machine:
 *   requested → awaited → received → verified
 *
 * Renders only for matter_type ∈ {conveyancing, otp}.
 */
const CATALOG = [
  { key: "rates_clearance",  label: "Rates Clearance Certificate",        issuer: "Municipality" },
  { key: "levy_clearance",   label: "Levy Clearance Certificate",          issuer: "Body Corporate / HOA" },
  { key: "electrical_coc",   label: "Electrical Compliance Certificate",   issuer: "Master Electrician" },
  { key: "gas_coc",          label: "Gas Compliance Certificate",          issuer: "Authorised Gas Installer" },
  { key: "guarantee",        label: "Bank Guarantee / Cash Cover",         issuer: "Bond Attorney / Bank" },
  { key: "bond_document",    label: "Bond Registration Documents",         issuer: "Bond Attorney" },
];

const STATUSES = [
  { value: "requested", label: "Requested", chip: "bg-zinc-100 text-zinc-700" },
  { value: "awaited",   label: "Awaited",   chip: "bg-amber-100 text-amber-800" },
  { value: "received",  label: "Received",  chip: "bg-sky-100 text-sky-800" },
  { value: "verified",  label: "Verified",  chip: "bg-emerald-100 text-emerald-800" },
];

export default function ExternalDocumentsPanel({ matter, onChanged }) {
  const mtype = matter?.matter_type || matter?.service_type;
  const [items, setItems] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  const load = () => {
    if (!matter?.id) return;
    api.get(`/admin/matters/${matter.id}/external-documents`)
      .then((res) => setItems(res.data?.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { if (matter?.id && (mtype === "conveyancing" || mtype === "otp")) load(); /* eslint-disable-next-line */ }, [matter?.id]);

  if (mtype !== "conveyancing" && mtype !== "otp") return null;

  const requestDoc = async (doc_type) => {
    setBusyKey(doc_type);
    try {
      await api.post(`/admin/matters/${matter.id}/external-documents`, { doc_type });
      toast.success("External document requested");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const patchStatus = async (item, status) => {
    setBusyKey(item.id);
    try {
      await api.patch(`/admin/matters/${matter.id}/external-documents/${item.id}`, { status });
      toast.success(`${item.label} → ${status}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const removeDoc = async (item) => {
    if (!window.confirm(`Remove ${item.label} from tracking?`)) return;
    setBusyKey(item.id);
    try {
      await api.delete(`/admin/matters/${matter.id}/external-documents/${item.id}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const byType = Object.fromEntries(items.map((i) => [i.doc_type, i]));

  return (
    <div className="bg-white border border-zinc-200 mb-5" data-testid="external-docs-panel">
      <div className="px-5 py-3 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ExternalLink className="h-4 w-4" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">EH Inc · External Documents</div>
            <div className="text-sm font-medium">Tracked clearance &amp; compliance documents (Type 2)</div>
          </div>
        </div>
        <div className="text-[11px] text-zinc-400">Requested → Awaited → Received → Verified</div>
      </div>

      <ul className="divide-y divide-zinc-200">
        {CATALOG.map((c) => {
          const item = byType[c.key];
          const status = item?.status || null;
          const statusMeta = STATUSES.find((s) => s.value === status);
          return (
            <li key={c.key} className="px-5 py-4 flex items-center gap-4 flex-wrap"
                data-testid={`external-row-${c.key}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="text-sm font-medium text-zinc-900">{c.label}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Issuer: {c.issuer}</div>
                {item?.notes && (
                  <div className="text-[11px] text-zinc-600 mt-1 italic">{item.notes}</div>
                )}
              </div>

              {!item && (
                <Button size="sm" variant="outline" className="rounded-none"
                  onClick={() => requestDoc(c.key)} disabled={busyKey === c.key}
                  data-testid={`request-${c.key}-btn`}>
                  {busyKey === c.key
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Requesting…</>
                    : <><Plus className="h-3.5 w-3.5 mr-1.5" /> Request</>}
                </Button>
              )}

              {item && (
                <>
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 ${statusMeta?.chip || "bg-zinc-100 text-zinc-600"}`}
                        data-testid={`external-status-${c.key}`}>
                    {statusMeta?.label || status}
                  </span>
                  <select
                    className="text-xs border border-zinc-300 rounded-none px-2 py-1.5 bg-white"
                    value={status}
                    onChange={(e) => patchStatus(item, e.target.value)}
                    disabled={busyKey === item.id}
                    data-testid={`external-set-status-${c.key}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeDoc(item)}
                    className="text-zinc-400 hover:text-red-600 p-1"
                    title="Remove from tracking"
                    disabled={busyKey === item.id}
                    data-testid={`external-remove-${c.key}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
